import { Divider, FormHelperText, Typography } from "@mui/material";
import { MuiOtpInput } from "mui-one-time-password-input";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import PageLoader from "../../../../components/PageLoader";
import AsyncButton from "../../../../components/buttons/AsyncButton";
import LogoSection from "../../../../components/logoSection/LogoSection";
import { useUserContext } from "../../../../contexts/UserContext";
import useLoginMutation from "../../../../hooks/graphql-hooks/mutations/useLoginMutation";
import useRequestOtpMutation from "../../../../hooks/graphql-hooks/mutations/useRequestOtpMutation";
import { isNullOrEmpty } from "../../../../utils/helper";
import "./Otp.scss";

interface OtpProps {
  usedEmail: string;
}

const Otp = (props: OtpProps) => {
  const { t } = useTranslation();
  const { otpResponse } = useUserContext().user;
  const { usedEmail } = props;

  const [requestNewOtp, { loading: loadingRequestNewOtp }] =
    useRequestOtpMutation({ dataAction: () => setNewCodeSent(true) });

  const [login, { loading: loadingLogin, error }] = useLoginMutation({
    errorAction: () => {
      setOtpInput("");
      setLoginSent(false);
    },
  });

  const [otpInput, setOtpInput] = useState<string>("");
  const [newCodeSent, setNewCodeSent] = useState<boolean>(false);
  //helperState to prevent login from firing multiple times eventhough it shouldn't
  const [loginSent, setLoginSent] = useState<boolean>(false);

  const onChange = (value: string) => {
    if (otpInput.length === 6 || loginSent) return;

    setOtpInput(value);
  };

  const onCompleteOtpInput = (value: string) => {
    if (loginSent) return;

    setLoginSent(true);
    login({
      variables: {
        loginInput: {
          code: value,
          processId: otpResponse?.processId || "",
        },
      },
    }).catch(() => {});
  };

  const handleResendCode = () => {
    if (loadingRequestNewOtp || isNullOrEmpty(usedEmail)) return;

    requestNewOtp({ variables: { otpInput: { email: usedEmail } } }).catch(
      () => {},
    );
  };

  const renderResendCodeSection = () => {
    if (newCodeSent)
      return (
        <Typography sx={{ padding: "6px 8px" }}>
          {t("codeHasBeenSent", { usedEmail })}
        </Typography>
      );

    return (
      <AsyncButton
        className="login-box-content-cant-login-button"
        loading={loadingRequestNewOtp}
        disabled={newCodeSent}
        sendRequest={handleResendCode}
        variant="text"
      >
        {t("resendOtp")}
      </AsyncButton>
    );
  };

  return (
    <div className="otp-container">
      <div className="otp-container-content">
        <div className="otp-container-content-form">
          <div>
            <LogoSection variant="platbricks" />
            <Typography variant="h5">{t("enterOtp")}</Typography>
          </div>
          <div>
            <MuiOtpInput
              autoFocus
              length={6}
              validateChar={(char) => !isNaN(Number(char))}
              onComplete={onCompleteOtpInput}
              onChange={onChange}
              value={otpInput}
              TextFieldsProps={{ inputMode: "numeric" }}
            />
            {loadingLogin && <PageLoader text={t("loggingIn")} />}
            {error && <FormHelperText error>{t("otpInvalid")}</FormHelperText>}
          </div>
        </div>
      </div>
      <div className="login-box-content-cant-login">
        <Divider />
        {renderResendCodeSection()}
      </div>
    </div>
  );
};

export default Otp;
